import { useResource } from '@landsbankinn/l16n';
import { Column, useMedia } from '@landsbankinn/ui';
import React from 'react';
import { ExchangeSymbol } from '../../../../__generated__/globalTypes';
import type { fetchStocks } from '../../../graphql/__generated__/fetchStocks';
import type { fetchStocksIntradayHistory } from '../../../graphql/__generated__/fetchStocksIntradayHistory';
import { MarketsDisclaimer } from '../shared/markets-disclaimer/MarketsDisclaimer';
import {
  ListHeaderContainer,
  MarketsListTitle,
  MarketsListTitleMobile,
  MarketsSelectorTabs,
  MarketsSmall,
} from '../shared/MarketsComponents';
import { StocksMarketTable } from './stocks-market-table/StocksMarketTable';
import { StocksTradesTable } from './stocks-trades-table/StocksTradesTable';
import res from './Stocks.res';

interface IProps {
  stocks?: fetchStocks;
  intraday?: fetchStocksIntradayHistory;
  stockDetailPrefix: string;
  stockBuyUrl: (symbol: string) => string;
  disclaimerText: string;
}

export function Stocks({
  stocks,
  intraday,
  stockDetailPrefix,
  stockBuyUrl,
  disclaimerText,
}: IProps) {
  const { r } = useResource();
  const isMobile = useMedia('sm');

  const [stockDataType, setSelectedStockDataType] = React.useState(
    StockDataType.Market,
  );

  const changeDataType = (StockDataType: StockDataType) => {
    setSelectedStockDataType(StockDataType);
  };

  return (
    <>
      <ListHeaderContainer>
        <Column md={{ width: 5 / 12 }} lg={{ width: 5 / 12 }}>
          {!isMobile ? (
            <>
              <MarketsListTitle>{r(res.stocks)}</MarketsListTitle>
              <MarketsSmall>{r(res.disclaimer)}</MarketsSmall>
            </>
          ) : (
            <MarketsListTitleMobile>{r(res.nasdaq)}</MarketsListTitleMobile>
          )}
        </Column>
        <Column md={{ width: 8 / 12 }} lg={{ width: 5 / 12 }}>
          <MarketsSelectorTabs
            mobileLabel={r(res.selectInformation)}
            mobileInset
            onSelectTab={(val) => {
              changeDataType(Number(val));
            }}
            tabSpacing="40px"
            selectedItemId={stockDataType.toString()}
            items={[
              {
                id: StockDataType.Market.toString(),
                label: r(res.marketToday),
              },
              {
                id: StockDataType.FirstNorth.toString(),
                label: r(res.firstNorth),
              },
              {
                id: StockDataType.Trades.toString(),
                label: r(res.trades),
              },
            ]}
            color="rgb(5, 179, 192)"
          />
        </Column>
      </ListHeaderContainer>
      {stockDataType === StockDataType.Market && (
        <StocksMarketTable
          stocks={stocks}
          exchange={ExchangeSymbol.XICE}
          stockDetailPrefix={stockDetailPrefix}
          stockBuyUrl={stockBuyUrl}
        />
      )}
      {stockDataType === StockDataType.FirstNorth && (
        <StocksMarketTable
          stocks={stocks}
          exchange={ExchangeSymbol.FNIS}
          stockDetailPrefix={stockDetailPrefix}
          stockBuyUrl={stockBuyUrl}
        />
      )}
      {stockDataType === StockDataType.Trades && (
        <StocksTradesTable intraday={intraday} prefix={stockDetailPrefix} />
      )}

      <MarketsDisclaimer text={disclaimerText} />
    </>
  );
}

export enum StockDataType {
  Market,
  FirstNorth,
  Trades,
}
